import { useEffect, useState } from "react";
import DateRange from "../common/DateRange";
import Nav from "../common/Nav";
import Selector from "../common/Selector";
import SideBar from "../common/SideBar";

import { Bar, Doughnut } from "react-chartjs-2";

import {
  BarElement,
  CategoryScale,
  Chart,
  LinearScale,
  Tooltip,
  Legend,
  ArcElement,
  Title,
} from "chart.js";

import "../../styles/tracking.css";
import { FetchPermissions } from "../../functions/permissions";
import Loading from "../generic/Loading";
import Unautorized from "../generic/Unautorized";

const API_BASE_URL = process.env.REACT_APP_BE_BASE_URL;
const token = localStorage.getItem("accessToken");
const headersAuth = {
  Authorization: `Bearer ${token}`,
};

export const Tracking = () => {
  Chart.register(
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
    ArcElement,
    Title,
  );

  interface ChartDataProperties {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      backgroundColor: string[];
      borderColor: string;
      borderWidth: number;
    }[];
  }

  interface OptionType {
    value: string;
    label: string;
  }

  interface GraphMonth {
    totalHoursSpent: number;
    month: string;
  }

  interface GraphUrl {
    url: string;
    totalHoursSpent: number;
  }

  interface DataFromBE {
    averageTimeSpent: string;
    graphMonth: GraphMonth[];
    graphUrl: GraphUrl[];
  }

  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState(false);

  //Data from BE
  const [dataFromBE, setDataFromBE] = useState<DataFromBE>();

  //Selectors variables
  const [selectionOptionsUsernames, setSelectionOptionsUsernames] = useState<
    OptionType[]
  >([]);
  const [selectedOptionUsernames, setSelectedOptionUsernames] =
    useState<OptionType>();
  const [selectionToSendUsernames, setSelectionToSendUsernames] = useState<
    string[]
  >(["null"]);

  const selectAllUsernames = () => {
    setSelectionToSendUsernames(
      selectionOptionsUsernames.map((option) => option.value),
    );
  };

  const [isButtonClickedUsernames, setIsButtonClickedUsernames] =
    useState<boolean>(false); // Manage button state
  const handleButtonClickUsernames = () => {
    setIsButtonClickedUsernames(!isButtonClickedUsernames); // Toggle button state
  };

  const handleChangeUsernames = (selectedOption: OptionType | OptionType[]) => {
    // If no value is selected, set it to an empty string
    if (
      !selectedOption ||
      (Array.isArray(selectedOption) && selectedOption.length === 0)
    ) {
      setSelectionToSendUsernames(["null"]); // Adjust accordingly
    } else {
      const selectedValues = Array.isArray(selectedOption)
        ? selectedOption
        : [selectedOption];

      const selectedValuesStrings = selectedValues.map((option) =>
        String(option.value),
      );
      setSelectionToSendUsernames(selectedValuesStrings); // Keep the string array for sending
    }
  };

  const [selectionOptionsCompany, setSelectionOptionsCompany] = useState<
    OptionType[]
  >([]);
  const [selectedOptionCompany, setSelectedOptionCompany] =
    useState<OptionType>();
  const [selectionToSendCompany, setSelectionToSendCompany] = useState<
    string[]
  >(["null"]);

  const selectAllCompany = () => {
    setSelectionToSendCompany(
      selectionOptionsCompany.map((option) => option.value),
    );
  };

  const [isButtonClickedCompany, setIsButtonClickedCompany] =
    useState<boolean>(false); // Manage button state
  const handleButtonClickCompany = () => {
    setIsButtonClickedCompany(!isButtonClickedCompany); // Toggle button state
  };

  const handleChangeCompany = (selectedOption: OptionType | OptionType[]) => {
    // If no value is selected, set it to an empty string
    if (
      !selectedOption ||
      (Array.isArray(selectedOption) && selectedOption.length === 0)
    ) {
      setSelectionToSendCompany(["null"]); // Adjust accordingly
    } else {
      const selectedValues = Array.isArray(selectedOption)
        ? selectedOption
        : [selectedOption];

      const selectedValuesStrings = selectedValues.map((option) =>
        String(option.value),
      );
      setSelectionToSendCompany(selectedValuesStrings); // Keep the string array for sending
    }
  };

  const [selectionOptionsLocation, setSelectionOptionsLocation] = useState<
    OptionType[]
  >([]);
  const [selectedOptionLocation, setSelectedOptionLocation] =
    useState<OptionType>();
  const [selectionToSendLocation, setSelectionToSendLocation] = useState<
    string[]
  >(["null"]);

  const selectAllLocation = () => {
    setSelectionToSendLocation(
      selectionOptionsLocation.map((option) => option.value),
    );
  };

  const [isButtonClickedLocation, setIsButtonClickedLocation] =
    useState<boolean>(false); // Manage button state
  const handleButtonClickLocation = () => {
    setIsButtonClickedLocation(!isButtonClickedLocation); // Toggle button state
  };

  const handleChangeLocation = (selectedOption: OptionType | OptionType[]) => {
    // If no value is selected, set it to an empty string
    if (
      !selectedOption ||
      (Array.isArray(selectedOption) && selectedOption.length === 0)
    ) {
      setSelectionToSendLocation(["null"]); // Adjust accordingly
    } else {
      const selectedValues = Array.isArray(selectedOption)
        ? selectedOption
        : [selectedOption];

      const selectedValuesStrings = selectedValues.map((option) =>
        String(option.value),
      );
      setSelectionToSendLocation(selectedValuesStrings); // Keep the string array for sending
    }
  };

  //Date Picker
  const [startDate, setStartDate] = useState<string>("null");
  const [endDate, setEndDate] = useState<string>("null");
  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  //Get data fro selectors
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/tracking/selectors`, {
          headers: headersAuth,
        });

        const data = await response.json();

        const optionsUsername = data.usernames.map((username: any) => ({
          value: username,
          label: username,
        }));

        const optionsCompany = data.companyIds.map((companyIds: any) => ({
          value: companyIds,
          label: companyIds,
        }));

        const optionsShops = data.shops.map((shops: any) => ({
          value: shops.whereCode,
          label: shops.locationName,
        }));

        setSelectionOptionsUsernames(optionsUsername);
        setSelectionOptionsCompany(optionsCompany);
        setSelectionOptionsLocation(optionsShops);
      } catch (error) {
        console.error("Error fetching data: ", error);
      }
    };
    fetchData();
  }, []);

  //Get the data to display
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/tracking/dashboard?startDate=${startDate}&endDate=${endDate}&username=${selectionToSendUsernames}&companyId=${selectionToSendCompany}&whereCode=${selectionToSendLocation}`,
          {
            headers: headersAuth,
          },
        );

        const data = await response.json();
        console.log("trackingData: ", data);
        setDataFromBE(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data: ", error);
      }
    };
    fetchData();
  }, [
    selectionToSendCompany,
    selectionToSendLocation,
    selectionToSendUsernames,
    startDate,
    endDate,
  ]);

  // Handle Excel Export
  const handleExportToExcel = async () => {
    setExportLoading(true);
    setExportError(null);
    setExportSuccess(false);

    try {
      // Build query parameters
      const params = new URLSearchParams({
        startDate: startDate !== "null" ? startDate : "",
        endDate: endDate !== "null" ? endDate : "",
        username: selectionToSendUsernames.join(","),
        companyId: selectionToSendCompany.join(","),
        whereCode: selectionToSendLocation.join(","),
      });

      // Make the export request
      const response = await fetch(
        `${API_BASE_URL}/tracking/export/excel?${params}`,
        {
          headers: headersAuth,
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to export data");
      }

      // Get the blob from response
      const blob = await response.blob();

      // Extract filename from Content-Disposition header or generate one
      let filename = "tracking-export.xlsx";
      const contentDisposition = response.headers.get("Content-Disposition");
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportSuccess(true);

      // Reset success message after 3 seconds
      setTimeout(() => {
        setExportSuccess(false);
      }, 3000);
    } catch (error: any) {
      console.error("Export error:", error);
      setExportError(
        error.message || "Failed to export data. Please try again.",
      );
    } finally {
      setExportLoading(false);
    }
  };

  //Chart creation
  const [monthlyData, setMonthlyData] = useState<ChartDataProperties>({
    labels: [],
    datasets: [
      {
        label: "Evolução mensal",
        data: [],
        backgroundColor: ["green"],
        borderColor: "black",
        borderWidth: 2,
      },
    ],
  });

  const [urlDistribuition, setUrlDistribuition] = useState<ChartDataProperties>(
    {
      labels: [],
      datasets: [
        {
          label: "Distribuição por páginas",
          data: [],
          backgroundColor: ["green"],
          borderColor: "black",
          borderWidth: 2,
        },
      ],
    },
  );

  //Bar Chart Data (Monthly Distribuition)
  useEffect(() => {
    if (dataFromBE && dataFromBE?.graphMonth.length > 0) {
      setMonthlyData({
        labels: dataFromBE.graphMonth.map((data) => data.month),
        datasets: [
          {
            label: "Evolução mensal",
            data: dataFromBE.graphMonth.map((data) => data.totalHoursSpent),
            backgroundColor: ["#90aa51", "#677483", "#54834B", "#388697"],
            borderColor: "white",
            borderWidth: 1,
          },
        ],
      });
    } else {
      setMonthlyData({
        labels: [],
        datasets: [
          {
            label: "Evolução mensal",
            data: [],
            backgroundColor: ["#90aa51", "#677483", "#54834B", "#388697"],
            borderColor: "white",
            borderWidth: 1,
          },
        ],
      });
    }

    if (dataFromBE && dataFromBE?.graphUrl.length > 0) {
      setUrlDistribuition({
        labels: dataFromBE.graphUrl.map((data) => data.url),
        datasets: [
          {
            label: "Distribuição por url",
            data: dataFromBE.graphUrl.map((data) => data.totalHoursSpent),
            backgroundColor: ["#90aa51", "#677483", "#54834B", "#388697"],
            borderColor: "white",
            borderWidth: 1,
          },
        ],
      });
    } else {
      setUrlDistribuition({
        labels: [],
        datasets: [
          {
            label: "Distribuição por url",
            data: [],
            backgroundColor: ["#90aa51", "#677483", "#54834B", "#388697"],
            borderColor: "white",
            borderWidth: 1,
          },
        ],
      });
    }
  }, [dataFromBE]);

  var options_Bar = {
    scales: {
      x: {
        beginAtZero: true,
      },
    },
  };

  // Permission check
  useEffect(() => {
    const checkPermission = async () => {
      try {
        const permission = await FetchPermissions("view-pdgo", "Permission");
        setHasPermission(permission);
      } catch (error) {
        console.error("Error checking permissions:", error);
        setHasPermission(false);
      }
    };
    checkPermission();
  }, []);

  //   if (hasPermission === null) {
  //     return <Loading />;
  //   }

  //   if (!hasPermission) {
  //     return <Unautorized />;
  //   }

  return (
    <div className="container">
      <SideBar></SideBar>
      <main>
        <h1>Tracking</h1>
        <div className="type-selector" id="tracking-selectors">
          <Selector
            options={selectionOptionsUsernames}
            isMulti={true}
            onChange={handleChangeUsernames}
            value={selectedOptionUsernames}
            placeholder="Escolha o utilizador"
            selectAll={selectAllUsernames}
            isButtonClicked={false}
            handleButtonClick={handleButtonClickUsernames}
          ></Selector>
          <Selector
            options={selectionOptionsCompany}
            isMulti={true}
            onChange={handleChangeCompany}
            value={selectedOptionCompany}
            placeholder="Escolha a empresa"
            selectAll={selectAllCompany}
            isButtonClicked={false}
            handleButtonClick={handleButtonClickCompany}
          ></Selector>
          <Selector
            options={selectionOptionsLocation}
            isMulti={true}
            onChange={handleChangeLocation}
            value={selectedOptionLocation}
            placeholder="Escolha o Posto"
            selectAll={selectAllLocation}
            isButtonClicked={false}
            handleButtonClick={handleButtonClickLocation}
          ></Selector>

          <DateRange headers={""} onDateChange={handleDateChange}></DateRange>

          {/* Excel Export Button */}
          <div className="export-button-container">
            <button
              onClick={handleExportToExcel}
              disabled={exportLoading}
              className="export-excel-btn"
            >
              {exportLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  A exportar...
                </>
              ) : (
                <>
                  <i className="bi bi-file-excel me-2"></i>
                  Exportar para Excel
                </>
              )}
            </button>

            {/* Success and Error Messages */}
            {exportSuccess && (
              <div className="alert alert-success mt-2 mb-0 p-2">
                <i className="bi bi-check-circle me-2"></i>
                Ficheiro Excel descarregado com sucesso!
              </div>
            )}

            {exportError && (
              <div className="alert alert-danger mt-2 mb-0 p-2">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {exportError}
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <h2 className="card-header">Tempo total</h2>
          <p className="card-content">{dataFromBE?.averageTimeSpent}</p>
        </div>

        <div className="graphs-container">
          <div className="individual-graph">
            {monthlyData && monthlyData.labels.length > 0 ? (
              <Bar data={monthlyData} options={options_Bar} />
            ) : (
              <h2>Sem dados para exibir</h2>
            )}
          </div>

          <div className="individual-graph">
            {urlDistribuition && urlDistribuition.labels.length > 0 ? (
              <Doughnut data={urlDistribuition} />
            ) : (
              <h2>Sem dados para exibir</h2>
            )}
          </div>
        </div>
      </main>
      <div className="right-section">
        <Nav></Nav>
      </div>
    </div>
  );
};
